import isClassDiaryDemo from "./isClassDiaryDemo";
import theme from "./fixtures/theme.json";
import aclRules from "./fixtures/aclRules.json";
import aclRolesSeed from "./fixtures/aclRoles.json";
import aclPermissions from "./fixtures/aclPermissions.json";
import authenticate from "./fixtures/authenticate.json";
import schoolUser from "./fixtures/schoolUser.json";
import loginInfo from "./fixtures/loginInfo.json";
import settingsExternalInfo from "./fixtures/settings.json";
import demoClasses from "./fixtures/demoClasses.json";
import { buildDemoTeacherSchedule } from "./buildDemoTeacherSchedule";
import { buildDemoStudentsForClass } from "./buildDemoStudents";
import {
  buildDemoClassStageConcepts,
  buildDemoClassStages,
  saveDemoClassStage
} from "./buildDemoClassStages";
import {
  buildDemoStudentDirectory,
  filterDemoStudentDirectory
} from "./buildDemoStudentDirectory";
import {
  buildDemoOccurrenceSchedule,
  buildDemoOccurrenceTypes,
  buildDemoOccurrencesStudents
} from "./buildDemoOccurrences";
import {
  applyDemoOccurrenceSave,
  mergePersistedDemoOccurrences
} from "./demoOccurrencesPersistence";
import {
  buildDemoDisciplinesForClass,
  buildDemoExamsForTurma,
  buildDemoGradesStudents
} from "./buildDemoGrades";
import {
  applyDemoGradesSave,
  mergePersistedDemoGrades
} from "./demoGradesPersistence";
import { applyDemoGradesV2JobSave } from "./demoGradesV2Persistence";
import {
  buildDemoGradePeriods,
  buildDemoGradesV2Grid,
  buildDemoGradeEvaluation
} from "./buildDemoGradesV2";
import {
  notifyDemoGradesV2JobPosted,
  notifyDemoGradesV1JobPosted,
  notifyDemoOccurrenceJobPosted,
  notifyDemoClassStageJobPosted,
  takeDemoGradeJobsResponse
} from "./demoGradeJobQueue";
import {
  notifyDemoFrequencyJobPosted,
  takeDemoFrequencyJobsResponse
} from "./demoFrequencyJobQueue";
import {
  notifyDemoClassPlanJobPosted,
  takeDemoClassPlanJobsResponse
} from "./demoClassPlanJobQueue";
import {
  getDemoReportDownload,
  notifyDemoReportJobPosted,
  takeDemoReportJobsResponse
} from "./demoReportJobQueue";
import {
  notifyDemoSmartMinutesJobPosted,
  takeDemoSmartMinutesJobsResponse
} from "./demoSmartMinutesJobQueue";
import { getDemoJobHistory, markDemoJobRead } from "./demoJobHistory";
import { buildDemoGridFrequency } from "./buildDemoGridFrequency";
import {
  buildDemoIndReportConcepts,
  buildDemoIndReportData,
  buildDemoIndReportPeriods
} from "./buildDemoIndividualReport";
import { buildDemoStudentProfile } from "./buildDemoStudentProfile";
import {
  buildDemoStudentOccurrences,
  buildDemoOccurrenceStages,
  buildDemoOccurrenceAdvancedConfig,
  buildDemoClassOccurrenceHistory
} from "./buildDemoIndividualOccurrences";
import { buildDemoStudentSchedule } from "./buildDemoStudentSchedule";
import { buildDemoReportCards } from "./buildDemoStudentStatistics";
import {
  buildDemoChartStages,
  buildDemoPeriodPerformance,
  buildDemoOverallPerformance,
  buildDemoGradeDistribution
} from "./buildDemoStudentCharts";
import {
  buildDemoClassPlanGrid,
  saveDemoClassPlan
} from "./buildDemoClassPlan";
import {
  buildDemoTeachersList,
  buildDemoTeacher,
  buildDemoClassesForTeacher
} from "./buildDemoTeachers";
import {
  getDemoNotificationCount,
  getDemoNotificationsPage,
  getDemoNotificationsUnreadCount,
  markDemoNotificationRead
} from "./buildDemoNotifications";
import {
  buildDemoCommunicationReadResponse,
  buildDemoPendingAckCommunication,
  demoMarkAcknowledgeDone,
  getDemoCommunicationSubscriber,
  getDemoCommunicationsCount,
  getDemoCommunicationsSubscribersList,
  getDemoCommunicationsUnreadCount
} from "./buildDemoCommunicationDetail";
import { DEMO_ACK_SESSION_KEY } from "./resetDemo";
import {
  addDemoSupportTicket,
  getDemoSupportClasses,
  getDemoSupportReasons,
  getDemoSupportsCount,
  getDemoSupportsList,
  getDemoSupportTypes
} from "./buildDemoSupport";
import { buildDemoGeneralBoard } from "./buildDemoGeneralBoard";
import {
  DEFAULT_GENERAL_BOARD_CONFIG,
  normalizeGeneralBoardConfig
} from "../utils/generalBoardConfig";

let demoAclRoles = null;
let demoAclRolePermissions = null;
let demoAclClassRoles = null;
let demoAclNextRoleId = 100;
let demoLbSyncSchedule = {
  incremental: { hour: [8, 12, 16], minute: 0 },
  full: { hour: [22], minute: 0 }
};
let demoGeneralBoardConfig = normalizeGeneralBoardConfig(
  DEFAULT_GENERAL_BOARD_CONFIG
);

function ensureDemoAclState() {
  if (demoAclRoles) return;
  demoAclRoles = aclRolesSeed.map(role => ({ ...role }));
  demoAclRolePermissions = {
    1: [101, 102, 104, 109],
    2: [101, 102, 103, 104, 105, 109],
    3: [106],
    4: [101, 102, 103, 104, 105, 106, 109]
  };
  demoAclClassRoles = {};
  demoAclNextRoleId = 100;
}

function joinBaseAndUrl(baseURL, url) {
  if (!url) return baseURL || "";
  if (url.startsWith("http")) return url;
  const b = (baseURL || "").replace(/\/+$/, "");
  const p = url.replace(/^\/+/, "");
  return `${b}/${p}`;
}

/** Path segment after .../api/ (no query string). */
function apiRelativePath(config) {
  const full = joinBaseAndUrl(config.baseURL, config.url || "").split("?")[0];
  const match = full.match(/\/api\/(.+)$/i);
  return match ? match[1].replace(/\/$/, "") : full.replace(/^\//, "");
}

/**
 * Axios pode já ter serializado `params` em `config.url` antes do adapter;
 * nesse caso `config.params.start` vem vazio e precisamos ler a query string.
 */
function getQueryParam(config, key) {
  if (
    config.params &&
    Object.prototype.hasOwnProperty.call(config.params, key)
  ) {
    const fromParams = config.params[key];
    if (fromParams !== "" && fromParams != null) {
      return fromParams;
    }
    if (fromParams === 0 || fromParams === false) {
      return fromParams;
    }
  }

  const full = joinBaseAndUrl(config.baseURL, config.url || "");
  const qi = full.indexOf("?");
  if (qi === -1) {
    return undefined;
  }
  const sp = new URLSearchParams(full.slice(qi + 1));
  const v = sp.get(key);
  return v == null || v === "" ? undefined : v;
}

function notMocked(config, method, path) {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.warn(
      `[class-diary demo] Unmocked ${method.toUpperCase()} ${path} — add a fixture in createDemoAdapter.js`
    );
  }
}

function parsePostBody(config) {
  const d = config.data;
  if (d == null || d === "") {
    return {};
  }
  if (typeof d === "object") {
    return d;
  }
  try {
    return JSON.parse(String(d));
  } catch {
    return {};
  }
}

/** Params `filter` do GET Academics/teachers (axios). */
function getTeachersFilter(config) {
  const f = config.params && config.params.filter;
  if (!f || typeof f !== "object") {
    return { searchText: "", classId: "" };
  }
  return {
    searchText: f.searchText != null ? String(f.searchText) : "",
    classId: f.classId != null ? String(f.classId) : ""
  };
}

/**
 * @param {import('axios').default.Adapter} xhrAdapter axios/xhr adapter
 */
export default function createDemoAdapter(xhrAdapter) {
  return function demoAdapter(config) {
    if (!isClassDiaryDemo()) {
      return xhrAdapter(config);
    }

    const method = (config.method || "get").toLowerCase();
    const path = apiRelativePath(config);

    const ok = (data, status = 200) =>
      Promise.resolve({
        data,
        status,
        statusText: "OK",
        headers: {},
        config,
        request: {}
      });

    // --- Auth & user bootstrap (waybee-api) ---
    if (method === "post" && path === "WaybeeUsers/Authenticate") {
      return ok(authenticate);
    }
    if (method === "post" && path === "WaybeeUsers/logout") {
      return ok({});
    }
    if (method === "get" && /^WaybeeUsers\/\d+\/schoolUser$/.test(path)) {
      return ok(schoolUser);
    }
    if (method === "get" && path === "ClassDiaryV1/user/getLoginInfo") {
      return ok(loginInfo);
    }
    if (method === "post" && path === "ClassDiaryV1/auth/forgot-password") {
      return ok({
        ok: true,
        message:
          "Se o usuário existir e tiver e-mail cadastrado no RM, um token de recuperação será enviado."
      });
    }
    if (method === "post" && path === "ClassDiaryV1/auth/reset-password") {
      const payload = parsePostBody(config) || {};
      if (!payload.token || !payload.newPassword) {
        return Promise.reject(
          Object.assign(new Error("Informe o token e a nova senha"), {
            isAxiosError: true,
            response: {
              status: 400,
              data: { error: { message: "Informe o token e a nova senha" } },
              config
            },
            config
          })
        );
      }
      return ok({ ok: true });
    }
    if (method === "post" && path === "ClassDiaryV1/auth/change-password") {
      const payload = parsePostBody(config) || {};
      if (!payload.lastPassword || !payload.newPassword) {
        return Promise.reject(
          Object.assign(new Error("Preencha a senha atual e a nova senha."), {
            isAxiosError: true,
            response: {
              status: 400,
              data: {
                error: { message: "Preencha a senha atual e a nova senha." }
              },
              config
            },
            config
          })
        );
      }
      return ok({ ok: true });
    }
    if (method === "get" && path === "WaybeeUsers/getAcl") {
      return ok(aclRules);
    }

    // --- ACL management (WBSCR-749) ---
    ensureDemoAclState();

    if (method === "get" && path === "AclWaybee/role") {
      return ok(demoAclRoles);
    }
    if (method === "get" && path === "AclWaybee/app-roles") {
      return ok(demoAclRoles);
    }
    if (method === "get" && path === "AclWaybee/permission") {
      return ok(aclPermissions);
    }
    if (method === "get" && /^AclWaybee\/\d+\/rolePermissions$/.test(path)) {
      const roleId = Number(path.split("/")[1]);
      const ids = demoAclRolePermissions[roleId] || [];
      return ok(
        aclPermissions.filter(permission => ids.includes(permission.id))
      );
    }
    if (method === "post" && path === "AclWaybee/role") {
      const payload = parsePostBody(config);
      const id = demoAclNextRoleId;
      demoAclNextRoleId += 1;
      const role = {
        id,
        name: payload.name,
        description: payload.description || "",
        isSystem: false,
        counterpart: {
          aclRoleId: id,
          externalRoleCounterpartId: payload.counterpart
        }
      };
      demoAclRoles.unshift(role);
      demoAclRolePermissions[id] = (payload.permissions || []).map(item =>
        typeof item === "object" ? item.id : item
      );
      return ok({ role, counterpart: role.counterpart, permissions: [] });
    }
    if (method === "post" && path === "AclWaybee/role/update") {
      const payload = parsePostBody(config);
      const role = demoAclRoles.find(item => item.id === payload.id);
      if (role) {
        role.name = payload.name;
        role.description = payload.description;
        if (payload.counterpart != null) {
          role.counterpart = {
            aclRoleId: role.id,
            externalRoleCounterpartId: payload.counterpart
          };
        }
        const current = new Set(demoAclRolePermissions[role.id] || []);
        (payload.removedPermissions || []).forEach(item =>
          current.delete(item.id)
        );
        (payload.newPermissions || []).forEach(item => current.add(item.id));
        demoAclRolePermissions[role.id] = Array.from(current);
      }
      return ok(role || {});
    }
    if (method === "put" && /^AclWaybee\/role\/\d+\/delete$/.test(path)) {
      const roleId = Number(path.split("/")[2]);
      const role = demoAclRoles.find(item => item.id === roleId);
      if (role && role.isSystem) {
        const error = new Error("Perfis de sistema não podem ser excluídos");
        error.response = {
          status: 400,
          data: {
            error: { message: "Perfis de sistema não podem ser excluídos" }
          }
        };
        return Promise.reject(error);
      }
      demoAclRoles = demoAclRoles.filter(item => item.id !== roleId);
      delete demoAclRolePermissions[roleId];
      return ok({});
    }
    if (method === "get" && path === "AclWaybee/class-roles") {
      const classId = String(getQueryParam(config, "classId") || "");
      const roleIds = demoAclClassRoles[classId] || [];
      return ok(demoAclRoles.filter(role => roleIds.includes(role.id)));
    }
    if (method === "put" && path === "AclWaybee/class-roles") {
      const payload = parsePostBody(config);
      const classId = String(payload.classId || "");
      const roleIds = (payload.roleIds || [])
        .map(Number)
        .filter(id => demoAclRoles.some(role => role.id === id));
      demoAclClassRoles[classId] = roleIds;
      return ok({
        classId,
        roles: demoAclRoles.filter(role => roleIds.includes(role.id)),
        added: roleIds.length,
        removed: 0,
        unchanged: 0
      });
    }

    if (method === "get" && path === "WaybeeUsers/mySessionInfo") {
      return ok({
        communicationsUnread: getDemoCommunicationsUnreadCount(),
        notificationsUnread: getDemoNotificationsUnreadCount()
      });
    }

    // --- Settings ---
    if (method === "get" && path === "Settings/theme") {
      return ok(theme);
    }
    if (method === "get" && path === "Settings/i18n") {
      return ok(null);
    }
    if (method === "get" && path === "Settings/getExternalInfo") {
      return ok(settingsExternalInfo);
    }
    if (method === "get" && path === "Settings/forgotPasswordSupport") {
      return ok({ phone: null, email: null });
    }

    // --- Communications (home widget + histórico na demo) ---
    if (
      method === "get" &&
      path === "CommunicationSubscribers/myCommunications"
    ) {
      let page = 0;
      if (
        config.params &&
        Object.prototype.hasOwnProperty.call(config.params, "page")
      ) {
        const p = config.params.page;
        page = p === "" || p == null ? 0 : Number(p);
      } else {
        const q = getQueryParam(config, "page");
        page = q === "" || q == null ? 0 : Number(q);
      }
      if (page === 0) {
        return ok(getDemoCommunicationsSubscribersList());
      }
      return ok([]);
    }
    if (
      method === "get" &&
      path === "CommunicationSubscribers/myCommunications/count"
    ) {
      return ok(getDemoCommunicationsCount());
    }
    if (
      method === "get" &&
      /^CommunicationSubscribers\/myCommunications\/.+$/.test(path)
    ) {
      const m = path.match(
        /^CommunicationSubscribers\/myCommunications\/([^/]+)$/
      );
      const segment = m ? m[1] : "";
      if (segment && segment !== "count") {
        return ok(getDemoCommunicationSubscriber(segment));
      }
      notMocked(config, method, path);
      return ok(null, 404);
    }
    if (
      method === "get" &&
      path === "CommunicationInteractions/getPendingAckCommunications"
    ) {
      if (
        typeof sessionStorage !== "undefined" &&
        sessionStorage.getItem(DEMO_ACK_SESSION_KEY) === "1"
      ) {
        return ok({ response: null });
      }
      return ok({ response: [buildDemoPendingAckCommunication()] });
    }
    if (method === "post" && path === "CommunicationInteractions/updateAck") {
      demoMarkAcknowledgeDone();
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(DEMO_ACK_SESSION_KEY, "1");
      }
      return ok({});
    }
    if (method === "put" && /^Communications\/[^/]+\/read$/.test(path)) {
      const m = path.match(/^Communications\/([^/]+)\/read$/);
      const communicationId = m ? m[1] : "";
      return ok(buildDemoCommunicationReadResponse(communicationId));
    }
    if (method === "put" && /^Communications\/[^/]+\/interaction$/.test(path)) {
      return ok({});
    }

    // --- Jobs queue (App toolbar + conclusão simulada após salvar Notas V2 / Frequência) ---
    if (method === "get" && path === "Jobs/myQueue") {
      const smartJobs = takeDemoSmartMinutesJobsResponse();
      // Aplicar draft + voz aqui (não no job queue) evita ciclo de imports.
      smartJobs.forEach(job => {
        if (
          job &&
          job.status === "success" &&
          job.name === "process_smart_minutes"
        ) {
          // eslint-disable-next-line global-require
          require("../views/SmartMinutes/applySmartMinutesJob").applySmartMinutesJobWithVoice(
            job
          );
        }
      });
      const demoJobs = takeDemoGradeJobsResponse()
        .concat(takeDemoFrequencyJobsResponse())
        .concat(takeDemoClassPlanJobsResponse())
        .concat(takeDemoReportJobsResponse())
        .concat(smartJobs);
      if (demoJobs.length) {
        return ok(demoJobs);
      }
      return ok([]);
    }
    if (method === "get" && path === "Jobs/checkSyncLb") {
      return ok({
        lastSync: { lastRunAt: Date.now() - 3600000 },
        inProgress: false,
        error: null,
        startedAt: null,
        finishedAt: null,
        syncStrategy: "incremental-stable",
        schedule: demoLbSyncSchedule
      });
    }
    if (method === "post" && path === "Jobs/saveLbSyncSchedule") {
      const body = parsePostBody(config);
      const { incremental } = body;
      if (incremental && incremental.mode === "interval") {
        demoLbSyncSchedule = {
          incremental: {
            mode: "interval",
            everyMinutes: incremental.everyMinutes,
            windowStart: incremental.windowStart,
            windowEnd: incremental.windowEnd
          },
          full: body.full || { hour: [22], minute: 0 }
        };
        return ok(demoLbSyncSchedule);
      }
      demoLbSyncSchedule = incremental
        ? {
            incremental,
            full: body.full || null
          }
        : demoLbSyncSchedule;
      return ok(demoLbSyncSchedule);
    }
    if (method === "get" && path === "Jobs/syncLb") {
      return ok({ id: `demo-sync-lb-${Date.now()}` });
    }
    if (method === "post" && /^Jobs\/[^/]+\/read$/.test(path)) {
      const jobId = path.match(/^Jobs\/([^/]+)\/read$/)[1];
      markDemoJobRead(jobId);
      return ok({});
    }
    if (method === "get" && /^Jobs\/[^/]+\/download$/.test(path)) {
      const jobId = path.match(/^Jobs\/([^/]+)\/download$/)[1];
      return ok(getDemoReportDownload(jobId));
    }
    if (method === "get" && path === "Jobs/myHistory") {
      const limit = Number(config.params && config.params.limit) || 20;
      const skip = Number(config.params && config.params.skip) || 0;
      const job = config.params && config.params.job;
      const status = config.params && config.params.status;
      return ok(getDemoJobHistory({ limit, skip, job, status }));
    }

    // --- Class diary (list + ACL by class) ---
    if (method === "get" && path === "ClassDiaryV1/class") {
      const search = (config.params && config.params.search) || "";
      const limit = config.params && config.params.limit;
      const skip = Number((config.params && config.params.skip) || 0);
      const series = config.params && config.params.series;
      let items = series
        ? demoClasses.filter(c => c.series === series)
        : demoClasses;
      if (search) {
        const needle = String(search).toLowerCase();
        items = items.filter(
          c =>
            String(c.name || "")
              .toLowerCase()
              .includes(needle) ||
            String(c.id || "")
              .toLowerCase()
              .includes(needle)
        );
      }
      if (limit != null && limit !== "") {
        const pageSize = Number(limit) || 30;
        return ok({
          items: items.slice(skip, skip + pageSize),
          total: items.length
        });
      }
      return ok(items);
    }
    if (method === "get" && /^ClassDiaryV1\/class\/[^/]+\/acl$/.test(path)) {
      return ok(aclRules);
    }
    if (
      method === "get" &&
      /^ClassDiaryV1\/class\/[^/]+\/students$/.test(path)
    ) {
      const m = path.match(/^ClassDiaryV1\/class\/([^/]+)\/students$/);
      const classId = m ? m[1] : "";
      return ok(buildDemoStudentsForClass(classId));
    }
    if (method === "get" && path === "ClassDiaryV1/students") {
      const search =
        (config.params && config.params.search) ||
        (config.params && config.params.filter) ||
        "";
      const limit = config.params && config.params.limit;
      const skip = Number((config.params && config.params.skip) || 0);
      const directory = search
        ? filterDemoStudentDirectory(search)
        : buildDemoStudentDirectory();
      if (limit != null && limit !== "") {
        const pageSize = Number(limit) || 30;
        return ok({
          items: directory.slice(skip, skip + pageSize),
          total: directory.length
        });
      }
      return ok(directory);
    }
    if (
      method === "get" &&
      /^ClassDiaryV1\/student\/[^/]+\/profile$/.test(path)
    ) {
      const m = path.match(/^ClassDiaryV1\/student\/([^/]+)\/profile$/);
      const studentId = m ? m[1] : "";
      const profile = buildDemoStudentProfile(studentId);
      if (!profile) {
        return Promise.reject(
          Object.assign(new Error("Aluno não encontrado"), {
            isAxiosError: true,
            response: {
              status: 404,
              data: { error: { message: "Aluno não encontrado" } },
              config
            },
            config
          })
        );
      }
      return ok(profile);
    }
    if (
      method === "get" &&
      /^ClassDiaryV1\/student\/[^/]+\/occurrences$/.test(path)
    ) {
      const m = path.match(/^ClassDiaryV1\/student\/([^/]+)\/occurrences$/);
      const studentId = m ? m[1] : "";
      const codPerlet = getQueryParam(config, "codPerlet");
      const codOccurrenceType = getQueryParam(config, "codOccurrenceType");
      const codOccurrenceReason = getQueryParam(config, "codOccurrenceReason");
      return ok(
        buildDemoStudentOccurrences(
          studentId,
          codPerlet,
          codOccurrenceType,
          codOccurrenceReason
        )
      );
    }
    if (method === "get" && path === "ClassDiaryV1/stages") {
      const classId = getQueryParam(config, "classId");
      return ok(buildDemoOccurrenceStages(classId));
    }

    // --- Notas V1 (disciplinas + provas + notas + salvar → localStorage) ---
    if (
      method === "get" &&
      /^ClassDiaryV1\/class\/[^/]+\/discipline$/.test(path)
    ) {
      const m = path.match(/^ClassDiaryV1\/class\/([^/]+)\/discipline$/);
      return ok(buildDemoDisciplinesForClass(m ? m[1] : ""));
    }
    if (method === "get" && path === "ClassDiaryV1/grades/exams") {
      return ok(buildDemoExamsForTurma(getQueryParam(config, "idTurmaDisc")));
    }
    if (method === "get" && path === "ClassDiaryV1/grades") {
      const idTurmaDisc = getQueryParam(config, "idTurmaDisc");
      const idStep = getQueryParam(config, "idStep");
      const idExam = getQueryParam(config, "idExam");
      let students = buildDemoGradesStudents(idTurmaDisc, idStep, idExam);
      students = mergePersistedDemoGrades(
        idTurmaDisc,
        idStep,
        idExam,
        students
      );
      return ok(students);
    }
    if (method === "post" && path === "ClassDiaryV1/grades") {
      applyDemoGradesSave(parsePostBody(config));
      return ok([]);
    }
    if (method === "post" && path === "ClassDiaryV1/grades/job") {
      const body = parsePostBody(config);
      applyDemoGradesSave(body);
      notifyDemoGradesV1JobPosted(body);
      return ok({});
    }

    // --- Notas V2 (períodos + grade + salvar → localStorage + job simulado) ---
    if (method === "get" && path === "ClassDiaryV1/periods") {
      const idTurmaDisc = getQueryParam(config, "idTurmaDisc");
      return ok(buildDemoGradePeriods(idTurmaDisc));
    }
    if (method === "get" && path === "ClassDiaryV1/grades-v2") {
      const idTurmaDisc = getQueryParam(config, "idTurmaDisc");
      const period = getQueryParam(config, "period");
      return ok(buildDemoGradesV2Grid(idTurmaDisc, period));
    }
    if (method === "get" && path === "ClassDiaryV1/evaluation") {
      const idTurmaDisc = getQueryParam(config, "idTurmaDisc");
      const codEtapa = getQueryParam(config, "codEtapa");
      const codProva = getQueryParam(config, "codProva");
      return ok(buildDemoGradeEvaluation(idTurmaDisc, codEtapa, codProva));
    }
    if (method === "post" && path === "ClassDiaryV1/grades-v2/job") {
      const body = parsePostBody(config);
      applyDemoGradesV2JobSave(body);
      notifyDemoGradesV2JobPosted(body);
      return ok({});
    }
    if (method === "post" && path === "ClassDiaryV1/frequency/job") {
      const body = parsePostBody(config);
      notifyDemoFrequencyJobPosted(body);
      return ok({});
    }
    if (method === "post" && path === "ClassDiaryV1/generate-report/job") {
      const body = parsePostBody(config);
      notifyDemoReportJobPosted(body);
      return ok({ id: `demo-report-job-${Date.now()}` });
    }
    if (method === "post" && path === "ClassDiaryV1/smart-minutes/job") {
      const body = parsePostBody(config);
      notifyDemoSmartMinutesJobPosted(body);
      return ok({ id: `demo-smart-minutes-job-${Date.now()}` });
    }
    if (
      method === "post" &&
      path === "ClassDiaryV1/smart-minutes/attribute-speakers"
    ) {
      // Demo: devolve transcrição gold já dividida por falante (fixture Alice/Roberta).
      /* eslint-disable global-require */
      const {
        ALICE_TRANSCRIPT
      } = require("../views/SmartMinutes/fixtures/mockProcessResult");
      /* eslint-enable global-require */
      const body = parsePostBody(config);
      return ok({
        meetingId: body && body.meetingId,
        draftId: body && body.draftId,
        transcriptText: ALICE_TRANSCRIPT,
        attributed: true,
        skipped: false,
        participantsDetected: [
          { name: "Elisa Professora Infantil 1", role: "professora" },
          { name: "Ana Coordenadora Infantil", role: "coordenação" },
          { name: "Roberta Martins Leite", role: "mãe" },
          { name: "Emanuel Mendes Leite", role: "pai" },
          { name: "Alice Rodrigues Leite", role: "aluna" }
        ]
      });
    }
    if (
      method === "post" &&
      path === "ClassDiaryV1/smart-minutes/voice/register-samples"
    ) {
      /* eslint-disable global-require */
      const {
        labRegisterVoiceSamples
      } = require("../views/SmartMinutes/voiceApiLab");
      /* eslint-enable global-require */
      const body = parsePostBody(config);
      return ok(
        labRegisterVoiceSamples({
          meetingId: body && body.meetingId,
          clusters: (body && body.clusters) || []
        })
      );
    }
    if (
      method === "post" &&
      path === "ClassDiaryV1/smart-minutes/voice/confirm-speaker"
    ) {
      /* eslint-disable global-require */
      const {
        labConfirmSpeakerVoice
      } = require("../views/SmartMinutes/voiceApiLab");
      /* eslint-enable global-require */
      const body = parsePostBody(config);
      return ok(
        labConfirmSpeakerVoice({
          meetingId: body && body.meetingId,
          speakerClusterId: body && body.speakerClusterId,
          personKey: body && body.personKey,
          displayName: body && body.displayName
        })
      );
    }
    if (
      method === "post" &&
      path === "ClassDiaryV1/smart-minutes/voice/suggest"
    ) {
      /* eslint-disable global-require */
      const {
        labSuggestSpeakersByVoice
      } = require("../views/SmartMinutes/voiceApiLab");
      /* eslint-enable global-require */
      const body = parsePostBody(config);
      return ok(
        labSuggestSpeakersByVoice({
          meetingId: body && body.meetingId,
          clusters: (body && body.clusters) || []
        })
      );
    }
    if (
      method === "post" &&
      path === "ClassDiaryV1/smart-minutes/voice/exclude-sample"
    ) {
      /* eslint-disable global-require */
      const {
        labExcludeVoiceSample
      } = require("../views/SmartMinutes/voiceApiLab");
      /* eslint-enable global-require */
      const body = parsePostBody(config);
      return ok(
        labExcludeVoiceSample({
          meetingId: body && body.meetingId,
          sampleId: body && body.sampleId,
          speakerClusterId: body && body.speakerClusterId,
          turnId: body && body.turnId
        })
      );
    }
    if (method === "post" && path === "ClassDiaryV1/test") {
      return ok({ success: true });
    }
    if (method === "post" && path === "ClassDiaryV1/delete-period") {
      return ok({ success: true });
    }

    // --- Ocorrências (aulas + tipos + alunos + salvar) ---
    if (
      method === "get" &&
      /^ClassDiaryV1\/class\/[^/]+\/occurrences\/schedule$/.test(path)
    ) {
      const m = path.match(
        /^ClassDiaryV1\/class\/([^/]+)\/occurrences\/schedule$/
      );
      return ok(buildDemoOccurrenceSchedule(m ? m[1] : ""));
    }
    if (method === "get" && path === "ClassDiaryV1/occurrences/types") {
      const classId = getQueryParam(config, "classId");
      const idTurmaDisc = getQueryParam(config, "idTurmaDisc");
      return ok(buildDemoOccurrenceTypes(classId, idTurmaDisc));
    }
    if (
      method === "get" &&
      path === "ClassDiaryV1/occurrences/advanced-config"
    ) {
      return ok(buildDemoOccurrenceAdvancedConfig());
    }
    if (
      method === "get" &&
      /^ClassDiaryV1\/class\/[^/]+\/occurrences\/history$/.test(path)
    ) {
      const m = path.match(
        /^ClassDiaryV1\/class\/([^/]+)\/occurrences\/history$/
      );
      return ok(
        buildDemoClassOccurrenceHistory(
          m ? m[1] : "",
          getQueryParam(config, "codPerlet")
        )
      );
    }
    if (method === "get" && path === "ClassDiaryV1/occurrences") {
      const idTurmaDisc = getQueryParam(config, "idTurmaDisc");
      const scheduleId = getQueryParam(config, "scheduleId");
      const occurrenceDate = getQueryParam(config, "occurrenceDate");
      let students = buildDemoOccurrencesStudents(idTurmaDisc, scheduleId);
      if (occurrenceDate) {
        students = mergePersistedDemoOccurrences(
          idTurmaDisc,
          scheduleId,
          occurrenceDate,
          students
        );
      }
      return ok(students);
    }
    if (method === "post" && path === "ClassDiaryV1/occurrences") {
      applyDemoOccurrenceSave(parsePostBody(config));
      return ok({ success: true });
    }
    if (method === "post" && path === "ClassDiaryV1/occurrences/job") {
      const body = parsePostBody(config);
      applyDemoOccurrenceSave(body);
      notifyDemoOccurrenceJobPosted(body);
      return ok({});
    }
    if (method === "get" && path === "ClassDiaryV1/occurrencesAttachments") {
      return ok({
        occurrenceId: getQueryParam(config, "occurrenceId"),
        occurrenceGroupId: 10,
        occurAttachment: []
      });
    }

    // --- Teacher schedule (home calendar + tela Horários: intervalo semanal → mosaico) ---
    if (
      method === "get" &&
      /^Academics\/teacher\/schedule(\/[^/]+)?$/.test(path)
    ) {
      const start = getQueryParam(config, "start") || new Date().toISOString();
      const end = getQueryParam(config, "end");
      return ok(buildDemoTeacherSchedule(start, end));
    }
    if (
      method === "get" &&
      /^Academics\/student\/schedule\/[^/]+$/.test(path)
    ) {
      const m = path.match(/^Academics\/student\/schedule\/([^/]+)$/);
      const studentId = m ? m[1] : "";
      const start = getQueryParam(config, "start");
      const end = getQueryParam(config, "end");
      return ok(buildDemoStudentSchedule(studentId, start, end));
    }
    if (
      method === "get" &&
      /^Academics\/student\/[^/]+\/report-cards-external$/.test(path)
    ) {
      const m = path.match(
        /^Academics\/student\/([^/]+)\/report-cards-external$/
      );
      return ok(buildDemoReportCards(m ? m[1] : ""));
    }
    if (
      method === "get" &&
      /^Academics\/student\/[^/]+\/statistics-stages(-external)?$/.test(path)
    ) {
      const m = path.match(
        /^Academics\/student\/([^/]+)\/statistics-stages(?:-external)?$/
      );
      return ok(buildDemoChartStages(m ? m[1] : ""));
    }
    if (
      method === "get" &&
      /^Academics\/student\/[^/]+\/period-performance(-external)?$/.test(path)
    ) {
      const m = path.match(
        /^Academics\/student\/([^/]+)\/period-performance(?:-external)?$/
      );
      return ok(buildDemoPeriodPerformance(m ? m[1] : ""));
    }
    if (
      method === "get" &&
      /^Academics\/student\/[^/]+\/overall-performance(-external)?$/.test(path)
    ) {
      const m = path.match(
        /^Academics\/student\/([^/]+)\/overall-performance(?:-external)?$/
      );
      return ok(buildDemoOverallPerformance(m ? m[1] : ""));
    }
    if (
      method === "get" &&
      /^Academics\/student\/[^/]+\/grade-distribuition(-external)?$/.test(path)
    ) {
      const m = path.match(
        /^Academics\/student\/([^/]+)\/grade-distribuition(?:-external)?$/
      );
      return ok(buildDemoGradeDistribution(m ? m[1] : ""));
    }

    // --- Frequência em grade (turma demo: horários gerados pelo intervalo de datas) ---
    if (method === "get" && path === "ClassDiaryV1/gridFrequency") {
      const idTurmaDisc = getQueryParam(config, "idTurmaDisc");
      const dateFrom = getQueryParam(config, "dateFrom");
      const dateTo = getQueryParam(config, "dateTo");
      return ok(buildDemoGridFrequency(idTurmaDisc, dateFrom, dateTo));
    }
    if (method === "get" && path === "ClassDiaryV1/frequency/settings") {
      return ok({
        showDoubleClasses: "0",
        highlights: [
          {
            id: "hl_desistente_freq_demo",
            order: 0,
            base: { kind: "desistente" },
            operator: "eq",
            value: "S",
            valueTo: "",
            color: "#D9D9D9",
            applyTo: { mode: "row", columns: [] }
          }
        ]
      });
    }
    if (method === "get" && path === "ClassDiaryV1/general-board/settings") {
      return ok(demoGeneralBoardConfig);
    }
    if (method === "put" && path === "ClassDiaryV1/general-board/settings") {
      demoGeneralBoardConfig = normalizeGeneralBoardConfig(
        demoGeneralBoardConfig,
        parsePostBody(config)
      );
      return ok(demoGeneralBoardConfig);
    }
    if (method === "get" && /^ClassDiaryV1\/general-board\/[^/]+$/.test(path)) {
      const classMatch = path.match(/^ClassDiaryV1\/general-board\/([^/]+)$/);
      return ok(
        buildDemoGeneralBoard(
          classMatch ? decodeURIComponent(classMatch[1]) : "DEMO-T1",
          getQueryParam(config, "period")
        )
      );
    }

    if (method === "get" && path === "ClassDiaryV1/class-stages/concepts") {
      return ok(buildDemoClassStageConcepts());
    }
    const classStagesMatch = path.match(
      /^ClassDiaryV1\/class-stages\/([^/]+)$/
    );
    if (classStagesMatch && method === "get") {
      return ok(buildDemoClassStages(decodeURIComponent(classStagesMatch[1])));
    }
    if (classStagesMatch && method === "post") {
      const stagesClassId = decodeURIComponent(classStagesMatch[1]);
      const body = parsePostBody(config);
      saveDemoClassStage(stagesClassId, body);
      notifyDemoClassStageJobPosted(stagesClassId, body);
      return ok({});
    }

    // --- Relatório individual (períodos, conceitos, grade por aluno) ---
    if (method === "get" && path === "ClassDiaryV1/individual-report/periods") {
      const idTurmaDisc = getQueryParam(config, "idTurmaDisc");
      return ok(buildDemoIndReportPeriods(idTurmaDisc));
    }
    if (
      method === "get" &&
      path === "ClassDiaryV1/individual-report/concepts"
    ) {
      return ok(buildDemoIndReportConcepts());
    }
    if (method === "get" && path === "ClassDiaryV1/individual-report") {
      const idTurmaDisc = getQueryParam(config, "idTurmaDisc");
      const period = getQueryParam(config, "period");
      return ok(buildDemoIndReportData(idTurmaDisc, period));
    }
    if (method === "post" && path === "ClassDiaryV1/individual-report") {
      return ok({ success: true });
    }

    // --- Plano de aula ---
    if (
      method === "get" &&
      /^ClassDiaryV1\/class\/[^/]+\/class-plan\/grid$/.test(path)
    ) {
      const idTurmaDisc = getQueryParam(config, "idTurmaDisc");
      const month = getQueryParam(config, "month");
      return ok(buildDemoClassPlanGrid(idTurmaDisc, month));
    }
    if (method === "post" && path === "ClassDiaryV1/class-plan/job") {
      const body = parsePostBody(config);
      saveDemoClassPlan(body);
      notifyDemoClassPlanJobPosted(body);
      return ok({});
    }

    // --- Professores (lista por turma + detalhe) ---
    if (method === "get" && path === "Academics/teachers") {
      const { searchText, classId } = getTeachersFilter(config);
      const filter = (config.params && config.params.filter) || {};
      const { limit } = filter;
      const skip = Number(filter.skip || 0);
      const teachers = buildDemoTeachersList(searchText, classId);
      if (limit != null && limit !== "") {
        const pageSize = Number(limit) || 30;
        return ok({
          items: teachers.slice(skip, skip + pageSize),
          total: teachers.length
        });
      }
      return ok(teachers);
    }
    if (
      method === "get" &&
      /^Academics\/teachers\/[^/]+\/classes$/.test(path)
    ) {
      const id = path.split("/")[2];
      return ok(buildDemoClassesForTeacher(id));
    }
    if (method === "get" && /^Academics\/teachers\/[^/]+$/.test(path)) {
      const id = path.split("/")[2];
      const teacher = buildDemoTeacher(id);
      return teacher
        ? ok(teacher)
        : Promise.reject(new Error("Professor não encontrado"));
    }

    // --- Reasonable defaults for other ClassDiary / Academics GETs used on navigation ---
    if (method === "get" && path.indexOf("ClassDiaryV1/") === 0) {
      notMocked(config, method, path);
      return ok([]);
    }
    if (method === "get" && path.indexOf("Academics/") === 0) {
      notMocked(config, method, path);
      return ok([]);
    }
    if (method === "get" && path === "Notifications/count") {
      return ok(getDemoNotificationCount());
    }
    if (method === "get" && path === "Notifications") {
      let page;
      if (
        config.params &&
        Object.prototype.hasOwnProperty.call(config.params, "page")
      ) {
        page = config.params.page;
      } else {
        page = getQueryParam(config, "page");
      }
      return ok(getDemoNotificationsPage(page));
    }
    if (method === "put" && /^Notifications\/[^/]+\/read$/.test(path)) {
      const m = path.match(/^Notifications\/([^/]+)\/read$/);
      const notificationId = m ? m[1] : "";
      return ok(markDemoNotificationRead(notificationId));
    }
    if (method === "get" && path.indexOf("Notifications/") === 0) {
      return ok({});
    }
    if (method === "get" && path === "Supports/types") {
      return ok(getDemoSupportTypes());
    }
    if (method === "get" && path === "Supports/reasons") {
      return ok(getDemoSupportReasons());
    }
    if (method === "get" && path === "Supports/classes") {
      return ok(getDemoSupportClasses());
    }
    if (method === "get" && path === "Supports/count") {
      return ok({ count: getDemoSupportsCount() });
    }
    if (method === "get" && path === "Supports") {
      return ok(getDemoSupportsList());
    }
    if (method === "post" && path === "Supports/sendSupportEmail") {
      let body = config.data;
      if (typeof body === "string") {
        try {
          body = JSON.parse(body);
        } catch (e) {
          body = {};
        }
      }
      addDemoSupportTicket(body || {});
      return ok({ success: true });
    }
    if (method === "put" && /^Supports\/[^/]+\/status$/.test(path)) {
      return ok({});
    }
    if (method === "get" && path.indexOf("CommunicationAttachments") === 0) {
      return ok([]);
    }

    notMocked(config, method, path);
    return Promise.reject(
      Object.assign(
        new Error(`Demo mode: unmocked API (${method.toUpperCase()} ${path})`),
        {
          config,
          isAxiosError: true,
          response: {
            status: 501,
            data: { error: { message: `Demo mode: add mock for ${path}` } },
            config
          }
        }
      )
    );
  };
}
